import { useState, createContext, useContext } from 'react'
import type { FCWithChildren, SearchSpecies } from '../types'
import { useDebounce } from '@uidotdev/usehooks'
import { createClient } from '../util/supabase/component'
import { type QueryFunctionContext, useQuery } from '@tanstack/react-query'

const INFO_COLS = [
  'has_germination',
  'has_oil',
  'has_protein',
  'has_dispersal',
  'has_seed_weights',
  'has_storage_behaviour',
  'has_morphology',
]

const queryFn = async ({ queryKey }: QueryFunctionContext<string[]>) => {
  const supabase = createClient()
  const input = queryKey[1]
  const [genus, species] = input.split(' ')

  let q = supabase
    .from('species_summary')
    .select('*')
    .or(INFO_COLS.map((col) => `${col}.eq.true`).join(','))
    .ilike('genus', `${genus}%`)
  if (species && species.length > 0) {
    q = q.ilike('epithet', `${species}%`)
  }
  const { data, error } = await q.order('genus').order('epithet')
  if (error) throw new Error(error.message)
  return (data as SearchSpecies[]) ?? []
}

const SpeciesSearchContext = createContext<{
  lastSearchResult: SearchSpecies[]
  lastSearchInput?: string
  setLastSearchInput: (arg: string) => void
}>({
  lastSearchResult: [],
  lastSearchInput: undefined,
  setLastSearchInput: (_) => {},
})

export const SpeciesSearchContextProvider: FCWithChildren = ({ children }) => {
  const [lastSearchInput, setLastSearchInput] = useState<string>('')

  const debouncedInput = useDebounce(lastSearchInput, 500)

  const { data } = useQuery({
    queryKey: ['search', debouncedInput],
    queryFn,
    enabled: Boolean(debouncedInput) && debouncedInput.length > 3,
    refetchOnMount: true,
  })

  return (
    <SpeciesSearchContext.Provider
      value={{
        lastSearchResult: data ?? [],
        lastSearchInput,
        setLastSearchInput,
      }}
    >
      {children}
    </SpeciesSearchContext.Provider>
  )
}

export const useSpeciesSearchContext = () => useContext(SpeciesSearchContext)
